import { Dispatch, SetStateAction, useEffect } from 'react';
import type { Message } from '../types';
import type { ConversationHistory } from './useConversationHistory';

type StaticWelcomeIntroParams = {
  welcomeConversationEnabled?: boolean;
  hasUserIntent?: boolean;
  transparencyCheckRequired: boolean;
  isLoadingHistory: boolean;
  buildIntroMessage(): Message;
  setHistory: Dispatch<SetStateAction<ConversationHistory>>;
};

/**
 * When the server-driven welcome conversation is disabled, no follow-up is
 * initiated — so once the transparency check is acknowledged a no-intent,
 * no-history user would be left with a blank screen. This seeds the static
 * introduction (name / no_name variant) as the pre-flag experience did,
 * skipping intent-led sessions (which produce their own first message) and
 * returning users (who have history). Because `buildIntroMessage` depends on
 * the user query, if the name arrives after the intro is first shown this
 * re-runs and upgrades the standalone intro from the no_name to the
 * personalised variant.
 */
export function useStaticWelcomeIntro({
  welcomeConversationEnabled,
  hasUserIntent,
  transparencyCheckRequired,
  isLoadingHistory,
  buildIntroMessage,
  setHistory,
}: StaticWelcomeIntroParams): void {
  useEffect(() => {
    if (
      welcomeConversationEnabled ||
      hasUserIntent ||
      transparencyCheckRequired ||
      isLoadingHistory
    ) {
      return;
    }

    setHistory(previous => {
      const hasOnlyIntro =
        previous.messages.length === 1 &&
        previous.messages[0].id === 'assistant-intro';

      // Never overwrite a real conversation — only seed or upgrade the
      // standalone intro.
      if (previous.messages.length > 0 && !hasOnlyIntro) {
        return previous;
      }

      return { ...previous, messages: [buildIntroMessage()] };
    });
  }, [
    welcomeConversationEnabled,
    hasUserIntent,
    transparencyCheckRequired,
    isLoadingHistory,
    buildIntroMessage,
    setHistory,
  ]);
}
